/**
 * The record-level conditions and list narrowings — the half of authorization that is code
 * (`docs/design/00-contracts.md` §11.3; conditions lifted from `02-security-rls.md` §6.4).
 *
 * What is **not** here is the role → action allow bit: that is per-organization data in
 * `role_permissions` (decision 0001), consulted by `can()` step 4. `RULES` answers the other
 * question — given that the organization has switched this action on for this role, what must
 * still be true of *this record*, and how must a permitted `list` be narrowed.
 *
 * Exactly one rule per action. An action absent from this map cannot be permitted by any data:
 * a new resource type or verb opens nothing until a rule is written for it (principle 2).
 */
import { z } from 'zod';

import { shareCovers } from '../contracts/performance-share.js';
import type { ActorRole } from '../enums.js';
import { TEMPLATE_ACTIONS } from './default-permissions.js';
import { guestLevelOn, guestPerformanceScopes, STAGE_PERFORMANCE_SCOPES } from './guest.js';
import { hasAgencyModule } from './types.js';
import type {
  Action,
  Actor,
  DenyReason,
  GuestScope,
  QueryScope,
  RecordResource,
  Resource,
} from './types.js';

/** A record-level condition. Returning a `DenyReason` denies with that reason. */
export type RuleCondition = (actor: Actor, resource: Resource) => true | DenyReason;

/** A narrowing for a permitted `list`. */
export type RuleScope = (actor: Actor, resource: Resource) => QueryScope;

export interface Rule {
  readonly action: Action;
  /**
   * Record-level condition. Branches on `actor.orgRole` where the matrix cell is conditional
   * rather than flat (`own`, `dept`, `part`, `grant`).
   */
  readonly when?: RuleCondition;
  /** Narrowing for `list` actions. Defaults to `{ kind: 'all' }`. Reads `actor.orgRole` itself. */
  readonly scope?: RuleScope;
}

const rec = (r: Resource): RecordResource | null => (r.kind === 'record' ? r : null);

const isAssignee: RuleCondition = (a, r) =>
  rec(r)?.assigneePersonId === a.personId ? true : 'not_assignee';

const isCreator: RuleCondition = (a, r) =>
  rec(r)?.createdByPersonId === a.personId ? true : 'not_creator';

/**
 * "This is mine": I raised it, I own it, or I am one of the people also on it.
 *
 * THE THIRD CLAUSE IS "MEDANSVARLIGE" (PAGES.md, Tasks [F1]), and it is not a widening of the
 * rule so much as the rule finally being able to see the whole of what it already meant. A
 * `limited` user told they are jointly responsible for something, who then cannot open it, has
 * been given a task in name only — and the failure would be silent, because the list would simply
 * not contain it.
 *
 * `participantPersonIds` rather than a new field: it already means "the people attached to this
 * record", which is exactly what a co-assignee is. The resolver fills it; a resource that has none
 * is unaffected, because `?.includes` on `undefined` is `undefined`.
 */
const isCreatorOrAssignee: RuleCondition = (a, r) => {
  const x = rec(r);
  if (!x) return 'not_creator';
  return x.createdByPersonId === a.personId ||
    x.assigneePersonId === a.personId ||
    x.participantPersonIds?.includes(a.personId) === true
    ? true
    : 'not_creator';
};

const isSelf: RuleCondition = (a, r) =>
  rec(r)?.subjectPersonId === a.personId ? true : 'not_creator';

/**
 * DETTE HUSET MÅ VÆRE ET BYRÅ (QA14 #3a, Atles avgjørelse).
 *
 * ------------------------------------------------------------------------------------------------
 * DEN ENE BETINGELSEN SOM IKKE SER PÅ RADEN, og det er meningen. Alle de andre her spør «er denne
 * raden min» eller «har jeg fått den delt»; denne spør hva slags organisasjon spørsmålet stilles
 * inne i. Provisjonen er forholdet mellom et byrå og en artist, og et arrangørhus har det ikke —
 * uansett hvilken rad de peker på og hvilken rolle de har.
 *
 * DERFOR ER DEN EN REGEL OG IKKE ET MENYVALG. Fram til QA14 sto kindet ett sted:
 * `settings-sections.ts` gjemte prissiden for et byrå med `kinds: ['festival']` — altså motsatt av
 * det den skulle — mens ruten, DTO-en og `/commission`-endepunktet svarte som før. Atle: «gjør
 * organisasjonskind til et vilkår i REGELLAGET, ikke bare i menyen, så ruta, DTO-en og endepunktet
 * alle svarer det samme.» Det er nøyaktig hva ett sted med ett svar betyr.
 *
 * Den virker på både `collection` og `record`, fordi den aldri leser `resource` — og det er akkurat
 * det `/commission` og avtalefanen trenger: den ene spør om én artist, den andre om huset.
 */
/*
 * SIDEN 239.40 ER DET MODULEN, IKKE TYPEN. En organisasjon med både Festival og Byrå har provisjon og
 * avtaler på artistene sine. Uten modulene på aktøren svarer `agencyModule`; typen spørres aldri
 * (246.6, `hasAgencyModule`).
 */
const isAgencyHouse: RuleCondition = (a) => (hasAgencyModule(a) ? true : 'not_an_agency');

const isParticipant: RuleCondition = (a, r) =>
  rec(r)?.participantPersonIds?.includes(a.personId) === true ? true : 'not_participant';

const PERMISSION_ORDER = { view: 0, edit: 1 } as const;

/**
 * ØKONOMI OG KONTRAKT DELES ALDRI (sak 232.10, Atle 29.09). Sak 72 lot en deling navngi dem; nå
 * gir et gammelt omfang `economy`/`contract` på en rad ingenting, og ingen fane med de navnene
 * åpnes gjennom en deling — uansett nivå, i `can()` og i basen (`app.guest_denied_tables`).
 */
const NEVER_SHARED_SCOPES: ReadonlySet<string> = new Set(['economy', 'contract']);

/** Et levende grant på DENNE raden som dekker et navngitt omfang (`scopes === null` er hele posten). */
const grantCoversScope =
  (scope: string, min: keyof typeof PERMISSION_ORDER): RuleCondition =>
  (a, r) => {
    const x = rec(r);
    if (!x) return 'grant_missing';
    const g = (a.guest?.grants ?? []).find((y) => y.objectType === x.type && y.objectId === x.id);
    if (!g) return 'grant_missing';
    // expiresAt is an ISO string, never a Date: an Actor crosses the wire to the browser.
    if (g.expiresAt !== null && new Date(g.expiresAt).getTime() <= Date.now()) {
      return 'grant_missing';
    }
    if (PERMISSION_ORDER[g.permission] < PERMISSION_ORDER[min]) return 'grant_insufficient';

    /* `shareCovers` er den ene definisjonen av hva et omfang dekker — siden og API-et spør den samme. */
    return shareCovers(g.scopes, scope) ? true : 'grant_insufficient';
  };

/**
 * «HVA DE SER» PÅ ÉN UNDERRESSURS AV EN PROGRAMPOST (QA runde 5, C-H2).
 *
 * `PolicyGuard` stiller dette spørsmålet ETTER `can()` på hver rute merket `@ShareScope(fane)`.
 * Bare for den som når posten gjennom en DELING — gjesten (sak 232). Medlemmer når programposten
 * gjennom matrisen. En collection har ingen deling å
 * snevre inn etter — der har `can()` allerede svart.
 *
 * Økonomi og kontrakt nektes alltid, uansett hva omfanget på raden sier (232.10).
 */
export function shareScopeAllows(
  actor: Actor,
  resource: Resource,
  scope: string,
): true | DenyReason {
  if (actor.orgRole !== 'guest') return true;
  if (NEVER_SHARED_SCOPES.has(scope)) return 'guest_never';
  if (resource.kind !== 'record' || resource.type !== 'performance') return true;
  /*
   * §5.3: EN PROGRAMPOST SOM BARE NÅS GJENNOM EN DELT SCENE viser tidene og den tekniske advancen —
   * ikke personene, reisen, overnattingen, oppgavene eller chatten. Basen gir det samme (1102).
   */
  if (actor.guest && reachedOnlyByStage(actor.guest, resource)) {
    return STAGE_PERFORMANCE_SCOPES.includes(scope) ? true : 'grant_insufficient';
  }
  /*
   * P5 (sak 232, T5): en gjest som når programposten gjennom en delt ÅRGANG har ingen deling på
   * selve posten, og ingen omfang — hele årgangen er delt, så hele programposten er det. Uten dette
   * var hver fane 403 for en årgangsgjest, mens `can()` sa ja til posten.
   */
  if (
    actor.orgRole === 'guest' &&
    actor.guest &&
    !actor.guest.grants.some((g) => g.objectType === 'performance' && g.objectId === resource.id) &&
    guestLevelOn(actor.guest, resource) !== null
  ) {
    return true;
  }
  /* 239.30: summen gjelder — en delt årgang er hele posten, også når posten er delt smalere direkte. */
  if (
    actor.guest &&
    guestLevelOn(actor.guest, resource) !== null &&
    guestPerformanceScopes(actor, resource.id, resource.editionId) === null
  ) {
    return true;
  }
  return grantCoversScope(scope, 'view')(actor, resource);
}

function reachedOnlyByStage(scope: GuestScope, resource: RecordResource): boolean {
  if (scope.reach?.has(`performance:${resource.id}`) !== true) return false;
  const { reach: _reach, ...withoutStage } = scope;
  return guestLevelOn(withoutStage, resource) === null;
}

/**
 * Rank order for the general escalation rule. Higher number = more authority.
 *
 * `guest` is lowest and NOT equal to `limited`: a guest is outside the organisation entirely, a
 * limited user is inside it with a narrow view.
 */
const ROLE_RANK: Readonly<Record<ActorRole, number>> = {
  /* Gjesten er ikke i organisasjonen og kan aldri gi eller ta en rolle (sak 232). */
  guest: 0,
  limited: 1,
  member: 2,
  admin: 3,
  superadmin: 4,
};

/**
 * THE ESCALATION RULE (byggeplan 1.1, Atle 27 August 2026).
 *
 * Nobody may grant, invite to, or revoke a role at or above their own level. `superadmin` is the
 * single exception, because an organisation must be able to hand over ownership — decision 0002
 * requires a superadmin to be able to promote another superadmin and demote themselves.
 *
 * This replaces an earlier version that hardcoded `if (actor.orgRole === 'admin')`. That was
 * enough while `membership:create` was denied to everyone below admin, and it was exactly the
 * wrong shape: the permission matrix is DATA, and an organisation that switches
 * `membership:create` on for `member` in Settings would have handed a member the ability to
 * create a superadmin. The rule has to be about rank, not about one named role, or layer 2 of
 * the access model can dig under layer 3.
 *
 * "At or above" rather than "above": an admin creating another admin is escalation too — it
 * multiplies the blast radius of one compromised account without anyone approving it.
 */
const roleChangeIsSafe: RuleCondition = (a, r) => {
  const row = rec(r);
  /*
   * Tittel, ansvar, rapporterer til: ingen rolle endres, altså ingenting å eskalere (M7). Men om en
   * SUPERADMIN er det sin egen celle (sak 239.36).
   */
  if (row?.roleUnchanged === true) return mayTouchSuperadmin(a, r);
  const target = row?.targetRole;
  if (!target) return 'role_not_permitted';
  if (a.orgRole === 'superadmin') return true;
  if (ROLE_RANK[target] >= ROLE_RANK[a.orgRole]) return 'cannot_grant_higher_role';
  /*
   * «…ELLER FJERNE EN» (ROLES.md §8). Regelen sjekket bare rollen som ble GITT, så en admin kunne
   * gjøre en annen admin til member med 200 — målt i QA runde 5 (H8). Rollen medlemmet HAR teller
   * like mye som rollen det får.
   */
  const current = row?.currentRole;
  if (current !== undefined && ROLE_RANK[current] >= ROLE_RANK[a.orgRole]) {
    return 'cannot_grant_higher_role';
  }
  return true;
};

/**
 * Å AVSLUTTE ET MEDLEMSKAP: CELLA AVGJØR HVEM, RANGEN HVEM MAN KAN TREFFE, OG ALDRI SITT EGET.
 *
 * QA15 blokk B2 (22. september) gjorde dette til «bare superadmin», skrevet som en rollesjekk her.
 * Atle, sak 196, 24. september: «Enig: bare superadmin, men som celle i Rettigheter» — og den
 * tverrgående regelen: «Tilgang hardkodes aldri.» Så HVEM som får avslutte et medlemskap er nå
 * cella `membership:delete` (justerbar, standard superadmin = ja, andre = nei; 0379 rettet husene
 * som fortsatt hadde `admin = ja` fra den gamle malen). Det som står igjen her er det som ikke er en
 * innstilling:
 *
 *   * ESKALERINGSREGELEN (ROLES.md §8): aldri noen på eller over egen rang. Åpner et hus cella for
 *     admin, kan en admin fjerne member og limited — ikke en annen admin.
 *   * `cannot_target_self`: en superadmin som deaktiverer seg selv låser seg ute av den ene skjermen
 *     som kunne ha angret det, uansett hvor mange superadminer huset har.
 */
const endsAMembershipBelowOwnRank: RuleCondition = (a, r) => {
  const target = rec(r);
  if (!target) return 'no_rule_matched';
  if (target.subjectPersonId === a.personId) return 'cannot_target_self';
  if (a.orgRole === 'superadmin') return true;
  const current = target.currentRole ?? target.targetRole;
  if (current === undefined) return 'role_not_permitted';
  if (ROLE_RANK[current] >= ROLE_RANK[a.orgRole]) return 'cannot_grant_higher_role';
  return true;
};

/**
 * OPPLYSNINGER OM EN SUPERADMIN (sak 239.36). Atle 04.10: «ligger vel i rettighets-innstillinger?»
 *
 * Tittel, ansvarsområde og «rapporterer til» på en superadmin endres bare av en superadmin, eller av
 * en rolle organisasjonen har gitt cella `superadmin_profile:update` (standard: ingen). Spurt om
 * samlingen — før raden er kjent — er svaret ja; raden avgjør.
 */
function mayTouchSuperadmin(a: Actor, r: Resource): true | DenyReason {
  const row = rec(r);
  if (row === null || a.orgRole === 'superadmin' || row.currentRole !== 'superadmin') return true;
  return a.permissions.allows(a.orgRole, 'superadmin_profile:update') ? true : 'role_not_permitted';
}

/**
 * EN CELLE I RETTIGHETER (sak 239.38). «Hvem som kan se og endre Rettigheter, blir en innstilling»,
 * og eskaleringsregelen (ROLES.md §8) følger med inn: den som ikke er superadmin, endrer bare
 * cellene til roller UNDER sin egen, og aldri cellene som sier hvem som ser og endrer Rettigheter —
 * ellers kunne en admin med cella gitt seg selv alt. Spurt om samlingen (å åpne siden og sende
 * kallet) er svaret ja; hver celle spørres som en rad med `targetRole` og `permissionAction`.
 */
const permissionChangeIsSafe: RuleCondition = (a, r) => {
  if (a.orgRole === 'superadmin') return true;
  const row = rec(r);
  if (row === null) return true;
  const target = row.targetRole;
  if (target === undefined) return 'role_not_permitted';
  if (ROLE_RANK[target] >= ROLE_RANK[a.orgRole]) return 'cannot_grant_higher_role';
  if (row.permissionAction?.startsWith('role_permission:') === true) return 'superadmin_only';
  return true;
};

/** Roles absent from the branch table are unconditional — the `yes` cells of the template. */
/**
 * `default` er grenen for alle roller som ikke er nevnt (sak 182).
 *
 * Alternativet var å skrive den samme grenen fire ganger, én per rolle. Den formen er en felle:
 * legges en femte rolle til, arver den stilltiende «alt», og det er den ene retningen et
 * rettighetssystem ikke skal kunne gli i av seg selv.
 */
type Branches<T> = Partial<Record<ActorRole, T>> & { readonly default?: T };

const byRole =
  (branches: Branches<RuleCondition>): RuleCondition =>
  (a, r) =>
    (branches[a.orgRole] ?? branches.default)?.(a, r) ?? true;

const ALL_ROWS: QueryScope = { kind: 'all' };

/** Roles absent from the branch table see everything the tenant can see. */
const scopeByRole =
  (branches: Branches<RuleScope>): RuleScope =>
  (a, r) =>
    (branches[a.orgRole] ?? branches.default)?.(a, r) ?? ALL_ROWS;

const ownRows: RuleScope = (a) => ({ kind: 'own', personId: a.personId });
const participantRows: RuleScope = (a) => ({ kind: 'participant', personId: a.personId });
/** Mine rader, og radene i avdelingene jeg leder (0357, 201.15). */
const ownOrDepartmentRows: RuleScope = (a) => ({
  kind: 'ownOrDepartments',
  personId: a.personId,
  departmentIds: [...a.departmentIds],
});

/**
 * RADEN LIGGER I EN AVDELING JEG LEDER (201.15). Resolveren gir avdelingen OG den over den, så
 * lederen for «Område» også svarer for «Område › Inngang».
 */
const inOwnDepartment: RuleCondition = (a, r) => {
  const ids = rec(r)?.departmentIds ?? [];
  return ids.some((id) => a.departmentIds.has(id)) ? true : 'not_in_department';
};

/**
 * EN FIL I EN ÅPEN MAPPE, ELLER I EN AVDELING JEG ER I — `document:list`s `departments`-scope sagt om
 * én post. Cella `document:read` er `dept` for `limited`; til sak 232 sto bare den gamle
 * `external`-grenen her, og cella hadde ingen vakt for rollen den faktisk gjaldt.
 */
const inOpenOrOwnDepartment: RuleCondition = (a, r) => {
  const ids = rec(r)?.departmentIds ?? [];
  return ids.length === 0 || ids.some((id) => a.departmentIds.has(id)) ? true : 'not_in_department';
};

/**
 * BARE DET ORGANISASJONEN SELV ER PART I KAN DELES (sak 232, EKSTERNE-OG-DELING §1).
 *
 * En rad i en ANNEN organisasjon (den delte advancen sett fra motparten) deles ikke videre. Steg 1
 * i `can()` nekter det allerede — ingen `*:share` står i `SHARED_ACTIONS` — men regelen står her
 * også, så den holder om lista en dag endres.
 *
 * NØKLET PÅ ORGANISASJONEN, IKKE PÅ `sharedWithActor` (E2E-RETT 30.09). `app.in_shared_performance`
 * er sann for BEGGE parter i en delt advance, også eieren — så flagget nektet festivalen å dele sin
 * egen programpost i det øyeblikket advancen var delt med byrået (403 på `GET …/shares`).
 */
const ownedByTheHouse: RuleCondition = (a, r) => {
  const x = rec(r);
  return x !== null && x.organizationId !== a.organizationId ? 'action_not_shared' : true;
};

/** Begge vilkårene må holde; det første avslaget er svaret. */
const both =
  (first: RuleCondition, second: RuleCondition): RuleCondition =>
  (a, r) => {
    const one = first(a, r);
    return one === true ? second(a, r) : one;
  };

const departmentRows: RuleScope = (a) => ({
  kind: 'departments',
  departmentIds: [...a.departmentIds],
});

/**
 * SAK 182 — «ROLLER UNDER NIVÅET SER BARE FESTIVALER/ÅRGANGER DE ER LAGT TIL I».
 *
 * Den ene innstillingen Atle ba om, lest ett sted. `festival:access_all` er boolsk per rolle i
 * `role_permissions` — hele nyansen bor her, som den gjør for `own` og `dept`.
 *
 * `assignedRows` er BÅDE lista og porten: den er scope for `list` og grunnlaget for `when` på
 * `read`. Ville de to vært skrevet hver for seg, kunne de kommet i utakt — og den utakten er
 * usynlig, for en rad du ikke ser i lista og likevel kan åpne på URL er akkurat det ingen tester.
 */
const seesEveryFestival = (a: Actor): boolean =>
  a.permissions.allows(a.orgRole, 'festival:access_all');

const assignedRows: RuleScope = (a) =>
  seesEveryFestival(a)
    ? ALL_ROWS
    : {
        kind: 'assigned',
        festivalIds: [...a.festivalAccess],
        editionIds: [...a.editionRoles.keys()],
      };

/**
 * Er DENNE raden innenfor det samme? `festivalId` på en årgang settes av resolveren.
 *
 * En rad uten noen av de to nøklene er ikke plassert i en festival i det hele tatt, og da har
 * spørsmålet ikke noe svar — svaret blir nei, som det skal være når regelen ikke kan avgjøre.
 */
const isAssignedFestival: RuleCondition = (a, r) => {
  if (seesEveryFestival(a)) return true;
  const x = rec(r);
  if (!x) return 'no_rule_matched';
  const festivalId = x.type === 'festival' ? x.id : x.festivalId;
  if (festivalId !== undefined && festivalId !== null && a.festivalAccess.has(festivalId)) {
    return true;
  }
  /*
   * Én-til-én og ikke `.has()`: `editionRoles` er nøklet på `EditionId`, mens `x.id` er en `Uuid`
   * uten merkelapp — raden er jo hva som helst her. Et `as`-kast ville skjult at de to er ulike.
   */
  if (x.type === 'edition') {
    for (const editionId of a.editionRoles.keys()) {
      if ((editionId as string) === (x.id as string)) return true;
    }
  }
  return 'not_assigned_festival';
};

const RULE_LIST: readonly Rule[] = [
  // ---------- organization ----------
  { action: 'organization:read' },
  { action: 'organization:update' },
  { action: 'organization:delete' },
  { action: 'organization:manage_roles', when: roleChangeIsSafe },
  /* Samarbeidspartnere (sak 239.21): legge til og trekke, og svare på en forespørsel. */
  { action: 'organization_partner:create' },
  { action: 'organization_partner:respond' },

  // ---------- memberships and invitations ----------
  { action: 'membership:list' },
  { action: 'membership:read' },
  { action: 'membership:create', when: roleChangeIsSafe },
  { action: 'membership:update', when: roleChangeIsSafe },
  /* Deaktiver, gjenopprett OG fjern — én rett, se `onlySuperadminEndsAMembership`. */
  { action: 'membership:delete', when: endsAMembershipBelowOwnRank },
  { action: 'membership:manage_roles' },
  /* Tittel, ansvarsområde og «rapporterer til» (QA12 #7); om en superadmin, se 239.36. */
  { action: 'member_profile:update', when: mayTouchSuperadmin },
  { action: 'superadmin_profile:update' },
  { action: 'invitation:list' },
  { action: 'invitation:create', when: roleChangeIsSafe },
  { action: 'invitation:delete' },

  // ---------- contacts ----------
  { action: 'contact:list', scope: scopeByRole({ limited: departmentRows }) },
  { action: 'contact:read' },
  { action: 'contact:create' },
  { action: 'contact:update' },
  { action: 'contact:delete' },
  { action: 'contact:invite' },
  { action: 'contact:merge' },

  /*
   * ---------- festivals ----------
   *
   * DE FIRE SOM LESER INNSTILLINGEN (sak 182). En gjest spørres aldri her: hen ser det som er
   * DELT (`guestCan`), ikke festivalene hen er ANSATT i.
   */
  {
    action: 'festival:list',
    scope: assignedRows,
  },
  {
    action: 'festival:read',
    when: isAssignedFestival,
  },
  { action: 'festival:create' },
  { action: 'festival:update' },
  { action: 'festival:delete' },
  /*
   * Å gi noen tilgang forutsetter at du selv ser festivalen. Uten `isAssignedFestival` her kunne en
   * rolle med `manage_roles`, men uten `access_all`, delt ut tilgang til en festival hen ikke ser.
   */
  { action: 'festival:manage_roles', when: isAssignedFestival },
  /*
   * Selve bryteren. Den spørres ALDRI om en rad — `can()` stiller den som et ja/nei om rollen, og
   * `seesEveryFestival` over er det eneste stedet den leses. Ingen `when`, ingen `scope`: hadde den
   * hatt en, ville den vært et svar på seg selv.
   */
  { action: 'festival:access_all' },

  // ---------- editions ----------
  {
    action: 'edition:list',
    scope: assignedRows,
  },
  {
    action: 'edition:read',
    when: isAssignedFestival,
  },
  { action: 'edition:create' },
  { action: 'edition:update' },
  { action: 'edition:archive' },
  { action: 'edition:delete' },
  { action: 'edition:share', when: ownedByTheHouse },
  /* F13: årgangens kart og område. */
  { action: 'map:share', when: ownedByTheHouse },

  /*
   * ---------- economy (sak 53) ----------
   * A plain role question, asked about the collection. There is no record to narrow it against —
   * `economy` is a view of rows that live elsewhere, and those rows keep their own cells — so no
   * `when` and no `scope`. The matrix answer IS the answer.
   */
  /*
   * ØKONOMI OG KONTRAKT DELES ALDRI (sak 232.10, Atle 29. september).
   *
   * Sak 72 ga den gamle rollen `external` en vei inn gjennom et grant som navnga `economy` eller
   * `contract`. Den er fjernet: to rene rollespørsmål, og matrisen svarer. En gjest når dem
   * aldri — `GUEST_NEVER` i `guest.ts` — og basen sier det samme (`app.guest_denied_tables`).
   * Byråer leser og signerer kontrakten i sin egen organisasjon (sak 112), ikke gjennom en deling.
   */
  { action: 'economy:read' },
  /*
   * The three writes, and they are collection questions for the same reason the read is: a cost
   * line has no cell of its own. `economy` is the resource, and one line of it is not a record the
   * matrix has an opinion about — «hvem får se pengene» is asked once, and writing is the same
   * question about the same money.
   */
  { action: 'contract:read' },
  { action: 'contract:update' },
  /* Den felles kontrakten (sak 274): rene rollespørsmål. Å signere krever i tillegg en fullmakt. */
  { action: 'contract:draft' },
  { action: 'contract:register' },
  { action: 'contract:void' },
  { action: 'contract:manage_roles' },

  /*
   * ---------- avtalen med artisten ----------
   *
   * INGEN SNEVRING, og fraværet er svaret: avtalen har ingen delingsvei, like lite som kontrakten
   * over (232.10). En avtale mellom byrået og artisten kan ingen utenfor byrået se, uansett hva noen skrur på.
   */
  /*
   * OG SIDEN QA14 #3a: BARE I ET BYRÅHUS.
   *
   * Malen gir `artist_agreement:read` til superadmin, admin og member i HVER organisasjon, fordi
   * malen ikke kjenner til hva slags hus den lages for. Følgen var tre åpne dører hos arrangøren:
   * feltet «Byråets provisjon (%)» i innstillingene, `commissionBp` på hver eneste registerrad, og
   * `GET /booking/register/:id/commission`, som lå på `performance:list` — cella enhver
   * festivaladmin har. `isAgencyHouse` lukker alle tre med det samme svaret, og det er hele
   * grunnen til at vilkåret står her og ikke i menyen.
   */
  /*
   * ARTISTEN I BYRÅETS STALL (sak 216.14 og 215.29). To celler, fordi Atle ba om begge som
   * innstillinger: «Invitere artist er en egen celle i Rettigheter, med medarbeider som standard»,
   * og «hvem som får [arkivere], er en celle i Rettigheter, ikke hardkodet». Bare i et byrå — en
   * festivals register har ingen stall å invitere fra, det er hustypen og ikke en rolle.
   */
  { action: 'artist:invite', when: isAgencyHouse },
  { action: 'artist:archive', when: isAgencyHouse },
  /*
   * BOOKING OG ARTISTER (G2, QA13 #87, 0923). Ingen radbetingelse: hvilket hus som har dem, avgjør
   * modulen i `can()` (`modules.ts`), og hvem i huset, avgjør cellene. «Slette artister i booking»
   * (sak 237.2) er `booking_artist:delete` — den dekker å slette en egen artist og å skjule en fra
   * skanningen (237.1).
   */
  { action: 'booking:list' },
  { action: 'booking:delete' },
  { action: 'booking_artist:create' },
  { action: 'booking_artist:update' },
  { action: 'booking_artist:delete' },
  { action: 'booking_draft:create' },
  { action: 'booking_draft:update' },
  { action: 'booking_enquiry:create' },
  { action: 'booking_bid:create' },
  { action: 'booking_bid:update' },
  { action: 'booking_bid:respond' },
  { action: 'booking_amount:read' },
  { action: 'artist:list' },
  { action: 'artist:update' },
  { action: 'agency_booking:create' },
  { action: 'agency_booking:update' },
  { action: 'agency_booking:respond' },
  // Overganger: ingen hustype-sperre. Registeret finnes i hvert hus; fanen står der registeret står.
  { action: 'agency_move:list' },
  { action: 'artist_agreement:read', when: isAgencyHouse },
  { action: 'artist_agreement:update', when: isAgencyHouse },
  // Spillesteder og konserter. Ingen hustype-sperre: «huset har det det har» (SPILLESTED-KONSERT §2).
  { action: 'venue:list' },
  { action: 'venue:read' },
  { action: 'venue:create' },
  { action: 'venue:update' },
  { action: 'venue:delete' },
  /* F18: salens spesifikasjon deles bare av huset som eier salen. */
  { action: 'venue:share', when: ownedByTheHouse },
  { action: 'venue:publish' },
  { action: 'venue_rate:read' },
  { action: 'venue_rate:update' },
  { action: 'venue_enquiry:list' },
  { action: 'venue_enquiry:read' },
  { action: 'venue_enquiry:respond' },
  { action: 'venue_night:list' },
  { action: 'venue_night:read' },
  { action: 'venue_night:create' },
  { action: 'venue_night:update' },
  { action: 'venue_night:delete' },
  { action: 'venue_night:share', when: ownedByTheHouse },
  { action: 'venue_registry:list' },
  { action: 'venue_registry:create' },
  { action: 'venue_registry:update' },
  { action: 'venue_registry:delete' },
  { action: 'date_request:list' },
  { action: 'date_request:create' },
  { action: 'date_request:update' },
  { action: 'concert:list' },
  { action: 'concert:read' },
  { action: 'concert:create' },
  { action: 'concert:update' },
  { action: 'concert:delete' },
  { action: 'concert_budget:read' },
  { action: 'concert_budget:update' },
  { action: 'concert_template:list' },
  { action: 'concert_template:create' },
  { action: 'concert_template:update' },
  { action: 'concert_template:delete' },

  { action: 'economy:create' },
  { action: 'economy:update' },
  { action: 'economy:delete' },

  // ---------- performances ----------
  // Plain role questions. A guest reaches one performance through `guestCan`, never through here.
  { action: 'performance:list' },
  { action: 'performance:read' },
  { action: 'performance:create' },
  { action: 'performance:update' },
  /*
   * Å SKRIVE I BOOKINGENS CHAT (QA 11. sep., P0.5).
   *
   * Handlingen sto i `Action`-typen og i `SHARED_ACTIONS` siden 0133, men hadde verken en regel
   * her eller en celle i matrisen. Deny-by-default gjorde da nøyaktig det den skal: NEKTET —
   * for alle, superadmin inkludert. Rapporten fant den som «Du kan lese chaten, men ikke skrive
   * i den», og kommentaren i `PerformanceChatService.canPost` påsto at cella fantes.
   *
   * Ingen `when`: den er et rent rollespørsmål, som `update`. Delingen avgjør hvilken RAD man
   * når — se `SHARED_ACTIONS` — ikke hva man får gjøre med den.
   */
  { action: 'performance:comment' },
  { action: 'performance:delete' },
  /* F22: godta motbudet og gi nytt bud. Et rent rollespørsmål; budet er alltid husets eget. */
  { action: 'performance:respond' },
  { action: 'performance:share', when: ownedByTheHouse },

  // Stages: structure, read by everybody including guests — a booking with no place is not a
  // booking — and written by the roles that own the festival's shape (decision A = 3, 29 August).
  { action: 'stage:list' },
  { action: 'stage:create' },
  { action: 'stage:update' },
  { action: 'stage:delete' },
  /* F14: kjøreplanen som lenke — bare det huset selv eier, som de andre delingene. */
  { action: 'stage:share', when: ownedByTheHouse },

  // ---------- accreditation levels ----------
  { action: 'accreditation_level:list' },
  { action: 'accreditation_level:create' },
  { action: 'accreditation_level:update' },
  { action: 'accreditation_level:delete' },

  // ---------- ressurslaget (trinn 1) ----------
  // Rene rollespørsmål, alle tolv. Det finnes ingen `own`-innsnevring her: en ressurs er ikke
  // «om» noen slik et skift er, og en tildeling hører til årgangen framfor til den som lagde den.
  { action: 'resource_type:list' },
  { action: 'resource_type:create' },
  { action: 'resource_type:update' },
  { action: 'resource_type:delete' },
  { action: 'resource:list' },
  { action: 'resource:create' },
  { action: 'resource:update', when: byRole({ limited: inOwnDepartment }) },
  { action: 'resource:delete' },
  /* Sak 232: begrenset deler bare i egen avdeling, som den endrer der og ikke ellers. */
  { action: 'resource:share', when: both(ownedByTheHouse, byRole({ limited: inOwnDepartment })) },
  { action: 'allocation:list' },
  { action: 'allocation:create', when: byRole({ limited: inOwnDepartment }) },
  { action: 'allocation:update', when: byRole({ limited: inOwnDepartment }) },
  { action: 'allocation:delete', when: byRole({ limited: inOwnDepartment }) },

  // ---------- deltakelseslaget (trinn 3) ----------
  // Feltene og rollene er rene rollespørsmål, som ressurstypene. `participation:list` har `own`
  // for `limited` av samme grunn som `crew_member:list`: en frivillig ser at de står oppført, og
  // ikke hele staben. Tjenesten gjør omfanget om til et `person_id = …` på unionen.
  { action: 'person_field:list' },
  { action: 'person_field:create' },
  { action: 'person_field:update' },
  { action: 'person_field:delete' },
  { action: 'participation_role:list' },
  { action: 'participation_role:create' },
  { action: 'participation_role:update' },
  { action: 'participation_role:delete' },
  { action: 'participation:list', scope: scopeByRole({ limited: ownRows }) },
  { action: 'participation:create' },
  { action: 'participation:update' },
  { action: 'participation:delete' },

  // ---------- People [F2] ----------
  // `own` for `limited` narrows to rows ABOUT them: a volunteer sees when they are on, not the
  // whole crew list. The service turns the scope into `person_id = …` on both tables.
  /* F34: masseutsending. Ingen radbetingelse — cella er hele svaret; mottakerne slås opp i huset. */
  { action: 'broadcast:create' },
  { action: 'broadcast:list' },
  { action: 'crew_member:list', scope: scopeByRole({ limited: ownRows }) },
  { action: 'crew_member:create' },
  { action: 'crew_member:update' },
  { action: 'crew_member:delete' },
  { action: 'shift:list', scope: scopeByRole({ limited: ownOrDepartmentRows }) },
  { action: 'shift:create', when: byRole({ limited: inOwnDepartment }) },
  { action: 'shift:update', when: byRole({ limited: inOwnDepartment }) },
  { action: 'shift:delete', when: byRole({ limited: inOwnDepartment }) },
  /* Bare på en vakt man selv står på — resolveren gir de som står der som `participantPersonIds`. */
  { action: 'shift:respond', when: byRole({ limited: isParticipant }) },
  { action: 'shift:share', when: both(ownedByTheHouse, byRole({ limited: inOwnDepartment })) },
  /* QA16 297.4: å be gjelder bare en selv (tjenesten leser `actor.personId`); å godkjenne er å bemanne. */
  { action: 'shift:request' },
  { action: 'shift:assign', when: byRole({ limited: inOwnDepartment }) },

  // ---------- People [F2]: frivillige ----------
  // NO SCOPES. Every cell above is yes-or-no, so there is nothing to narrow: a role that may read
  // the volunteer list reads all of it, and one that may not sees none of it. `own` would be
  // meaningless here — a volunteer's own signup is read through the public form's own route in
  // Bygg 3, not by giving them a cell on the organiser's list.
  { action: 'volunteer:list' },
  { action: 'volunteer:read' },
  { action: 'volunteer:create' },
  { action: 'volunteer:update' },
  { action: 'volunteer:delete' },
  { action: 'signup_form:list' },
  { action: 'signup_form:create' },
  { action: 'signup_form:update' },
  { action: 'signup_form:delete' },

  // ---------- People [F2]: akkreditering ----------
  // No scopes, for the reason the volunteer list has none: every cell is yes-or-no. The desk sees
  // the whole list or none of it — a half-list at the gate is worse than no list.
  { action: 'accreditation:list' },
  { action: 'accreditation:create' },
  { action: 'accreditation:update' },
  { action: 'accreditation:delete' },

  /*
   * ---------- documents and folders ----------
   *
   * `limited` gets `departments`, which the service reads as «folders with no department, plus the
   * folders of the departments I am in». The open-folder half is why this is not `ownRows`: a
   * volunteer who may see nothing but what they uploaded cannot find the evacuation plan, which is
   * the one document the product most needs them to find.
   */
  {
    action: 'document:list',
    scope: scopeByRole({ limited: departmentRows }),
  },
  { action: 'document:read', when: byRole({ limited: inOpenOrOwnDepartment }) },
  { action: 'document:create' },
  { action: 'document:delete', scope: scopeByRole({ limited: ownRows }) },
  /* F12: bare det organisasjonen selv eier. Kontrakt- og økonomimappene nekter basen og tjenesten. */
  { action: 'document:share', when: ownedByTheHouse },

  // The structure. No scope branch: a role either restructures the shelf or does not, and the
  // matrix says only owner and admin do.
  { action: 'folder:create' },
  { action: 'folder:update' },
  { action: 'folder:delete' },
  { action: 'folder:share', when: ownedByTheHouse },

  // ---------- tasks ----------
  {
    action: 'task:list',
    scope: scopeByRole({ limited: ownRows }),
  },
  {
    action: 'task:read',
    when: byRole({ limited: isCreatorOrAssignee }),
  },
  { action: 'task:create' },
  {
    action: 'task:update',
    when: byRole({ limited: isCreatorOrAssignee }),
  },
  { action: 'task:assign' },
  /*
   * COMMENTING IS ITS OWN CELL (sak 56): saying «ja, bussen er bestilt» on your own show is not
   * changing the show. A guest comments at the level `comment` (`policy/guest.ts`).
   *
   * `limited` matches `task:update`: the tasks you raised or own. The cell is `own` there too, so
   * the default behaviour is identical to the `task:update` this route used to ask for.
   */
  {
    action: 'task:comment',
    when: byRole({ limited: isCreatorOrAssignee }),
  },
  { action: 'task:complete', when: byRole({ limited: isAssignee }) },
  // 239.35: Begrenset sletter også sine egne (laget av eller ansvarlig for).
  {
    action: 'task:delete',
    when: byRole({ member: isCreatorOrAssignee, limited: isCreatorOrAssignee }),
  },

  /*
   * KALENDEREN (rettelse B12). Cellene er tasks', og narrowingen er den samme formen: en
   * `limited` ser det hen selv har laget eller er invitert til.
   *
   * INVITASJONEN TELLER, og det er forskjellen fra en oppgave: en hendelse har ingen «ansvarlig»,
   * den har DELTAKERE. Resolveren legger den innloggede i `assigneePersonId` når hen står på
   * deltakerlista, slik at den samme betingelsen kan brukes — se `CalendarEventResolver`.
   */
  { action: 'event:list', scope: scopeByRole({ limited: ownRows }) },
  {
    action: 'event:read',
    when: byRole({ limited: isCreatorOrAssignee }),
  },
  { action: 'event:create' },
  {
    action: 'event:update',
    when: byRole({ limited: isCreatorOrAssignee }),
  },
  { action: 'event:delete', when: byRole({ member: isCreatorOrAssignee }) },
  { action: 'task:restore' },
  { action: 'task:share', when: ownedByTheHouse },

  // ---------- inbox ----------
  {
    action: 'thread:list',
    scope: scopeByRole({ limited: participantRows }),
  },
  { action: 'thread:read', when: byRole({ limited: isParticipant }) },
  { action: 'thread:create' },
  { action: 'thread:update' },
  { action: 'thread:delete' },
  // Every role, superadmin included, only ever lists the messages of threads it participates in.
  { action: 'message:list', scope: participantRows },
  { action: 'message:create', when: byRole({ limited: isParticipant }) },
  { action: 'message:update', when: isCreator },
  { action: 'message:delete' },

  // ---------- taxonomy (principle 9: this is data, not code) ----------
  { action: 'tag:list' },
  { action: 'tag:create' },
  { action: 'tag:update' },
  { action: 'tag:delete' },
  { action: 'department:list' },
  { action: 'department:read' },
  { action: 'department:create' },
  { action: 'department:update' },
  { action: 'department:delete' },
  { action: 'department:share', when: ownedByTheHouse },

  // ---------- notifications (always self-scoped; RLS agrees) ----------
  { action: 'notification:list', scope: ownRows },
  { action: 'notification:read', when: isSelf },
  { action: 'notification:update', when: isSelf },

  // ---------- utkast (D9): bare forfatterens egne; RLS sier det samme ----------
  { action: 'draft:list', scope: ownRows },
  { action: 'draft:create' },
  { action: 'draft:delete', when: isCreator },
  { action: 'draft:send', when: isCreator },

  // ---------- sharing (access dimension 4) ----------
  { action: 'object_grant:list' },
  { action: 'object_grant:create' },
  { action: 'object_grant:delete' },
  /* Eksport: ingen radbetingelse. Cella er døra; dataene hentes gjennom tjenestene og RLS som skjermen. */
  { action: 'export:create' },
  /*
   * SAK 232: de tre cellene som ikke er «dele DETTE» — å invitere en ekstern, å lage en lenke, og å
   * dele med nivået «kan endre». Rene rollespørsmål; delingstjenesten (P3) spør dem i tillegg til
   * `<type>:share` på objektet.
   */
  { action: 'external:invite' },
  { action: 'share_link:create' },
  { action: 'share:edit_level' },
  /* F9: oversikten «Delinger». Ingen radbetingelse — den er hele organisasjonens. */
  { action: 'share:list' },
  /* F21: varsel om bud og forespørsler uten ansvarlig. Et rollespørsmål; lytteren spør basen. */
  { action: 'booking_enquiry:notify' },
  { action: 'shared_advance:read' },
  /*
   * F17: å dele den delte advancen videre. Resolveren svarer bare når organisasjonen er MOTPART i en
   * levende delt advance; eieren deler sin egen programpost med `performance:share`.
   */
  { action: 'shared_advance:share' },

  // ---------- oversight ----------
  { action: 'audit_log:list' },
  { action: 'audit_log:read' },
  { action: 'consent:list' },

  // ---------- the matrix itself ----------
  { action: 'role_permission:list' },
  { action: 'role_permission:read' },
  { action: 'role_permission:update', when: permissionChangeIsSafe },

  // ---------- self ----------
  { action: 'user:read', when: isSelf },
  { action: 'user:update', when: isSelf },
  { action: 'person:read' },
];

function indexRules(list: readonly Rule[]): ReadonlyMap<Action, Rule> {
  const map = new Map<Action, Rule>();
  for (const rule of list) {
    if (map.has(rule.action)) {
      // A second rule for one action would mean two answers to one question (§11.3).
      throw new Error(`Duplicate policy rule for action "${rule.action}".`);
    }
    map.set(rule.action, rule);
  }
  return map;
}

/** Exactly one rule per action. An action absent from this map cannot be permitted by any data. */
export const RULES: ReadonlyMap<Action, Rule> = indexRules(RULE_LIST);

/**
 * The action catalogue: every action that both has a rule and appears in the default template.
 * `role_permissions` rows are validated against this on the way in (§6.6a) — the database checks
 * the shape, the API checks membership of the catalogue.
 */
export const ALL_ACTIONS: readonly Action[] = Object.freeze(
  [...TEMPLATE_ACTIONS].filter((action) => RULES.has(action)).sort(),
);

const ALL_ACTION_SET: ReadonlySet<string> = new Set(ALL_ACTIONS);

export const isKnownAction = (value: unknown): value is Action =>
  typeof value === 'string' && ALL_ACTION_SET.has(value);

/** The strict counterpart of `actionSchema`: catalogue membership, not merely a valid shape. */
export const knownActionSchema: z.ZodType<Action> = z
  .custom<Action>(isKnownAction, { message: 'Unknown action.' })
  .meta({ id: 'KnownAction' });
